import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { SupportService } from '@/app/services/support/support.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { ImageService } from '@/app/services/image/image.service';
import {
  SupportAdminOption,
  SupportAlertPreferences,
  SupportAttachment,
  SupportTemplateMessage,
  SupportOutboundTicketCategory,
  SupportTicketDetail,
  SupportUserSummary,
  SupportUserType,
} from '@/app/common/general-types/support-ticket';
import { isUkSupportCountry } from '@/app/common/support-chat.util';
import { SUPPORT_OUTBOUND_TICKET_CATEGORY_OPTIONS } from '@/app/common/support-ticket-category.util';
import { applySupportTemplateMessage } from '@/app/common/support-template-message.util';
import { DialogComponent } from '@/app/atoms/dialog/dialog.component';
import { SupportAttachmentInputComponent } from '@/app/molecules/support-attachment-input/support-attachment-input.component';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

@Component({
  selector: 'app-support-outbound-ticket',
  imports: [FormsModule, DialogComponent, SupportAttachmentInputComponent],
  templateUrl: './support-outbound-ticket.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportOutboundTicket implements OnDestroy {
  readonly open = input(false);
  readonly admins = input<SupportAdminOption[]>([]);
  readonly templates = input<SupportTemplateMessage[]>([]);
  readonly closed = output<void>();
  readonly created = output<SupportTicketDetail>();

  readonly imageService = inject(ImageService);
  private readonly supportService = inject(SupportService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly cdr = inject(ChangeDetectorRef);

  subscriptionList = new Subscription();

  readonly categoryOptions = SUPPORT_OUTBOUND_TICKET_CATEGORY_OPTIONS;

  readonly results = signal<SupportUserSummary[]>([]);
  readonly showResults = signal(false);
  readonly searching = signal(false);
  readonly searchError = signal('');
  readonly selectedUser = signal<SupportUserSummary | null>(null);
  readonly highlightedIndex = signal(-1);
  readonly submitting = signal(false);
  readonly error = signal('');

  searchTerm = '';
  category: SupportOutboundTicketCategory | '' = '';
  assignedAdminUserId = '';
  selectedTemplateId = '';
  subject = '';
  message = '';
  attachments: SupportAttachment[] = [];
  alertPreferences: SupportAlertPreferences = {
    sms: false,
    whatsapp: false,
    email: true,
    push: false,
  };

  private readonly searchTerms = new Subject<string>();

  constructor() {
    this.subscriptionList.add(
      this.searchTerms
        .pipe(
          debounceTime(SEARCH_DEBOUNCE_MS),
          distinctUntilChanged(),
          tap(() => {
            this.searching.set(true);
            this.searchError.set('');
          }),
          switchMap((term) =>
            this.supportService.searchSupportUsers(term).pipe(
              catchError(() => {
                this.searchError.set('Could not search users. Please try again.');
                return of([] as SupportUserSummary[]);
              }),
            ),
          ),
        )
        .subscribe((users) => {
          this.results.set(users ?? []);
          this.highlightedIndex.set(-1);
          this.showResults.set(true);
          this.searching.set(false);
          this.cdr.markForCheck();
        }),
    );

    effect(() => {
      if (this.open()) {
        this.resetForm();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onSearchTermChange(term: string): void {
    this.searchTerm = term;
    const trimmed = term.trim();

    if (trimmed.length < MIN_SEARCH_LENGTH) {
      this.results.set([]);
      this.showResults.set(false);
      this.searching.set(false);
      this.highlightedIndex.set(-1);
      return;
    }

    this.searchTerms.next(trimmed);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const total = this.results().length;

    if (event.key === 'Escape') {
      this.showResults.set(false);
      this.highlightedIndex.set(-1);
      return;
    }

    if (!this.showResults() || total === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightedIndex.update((index) => (index + 1) % total);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedIndex.update((index) => (index <= 0 ? total - 1 : index - 1));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const user = this.results()[this.highlightedIndex()];
      if (user) {
        this.selectUser(user);
      }
    }
  }

  selectUser(user: SupportUserSummary): void {
    this.selectedUser.set(user);
    this.showResults.set(false);
    this.highlightedIndex.set(-1);
    this.error.set('');
    this.alertPreferences = this.defaultAlertPreferences(user);
    this.reapplySelectedTemplate();
  }

  changeUser(): void {
    this.selectedUser.set(null);
    this.showResults.set(this.results().length > 0);
  }

  optionId(index: number): string {
    return `outbound-user-option-${index}`;
  }

  activeOptionId(): string | null {
    const index = this.highlightedIndex();
    return index >= 0 ? this.optionId(index) : null;
  }

  optionClass(index: number): string {
    const base = 'flex w-full items-center gap-3 px-3 py-2 text-left cursor-pointer';
    return this.highlightedIndex() === index
      ? `${base} bg-gray-50 dark:bg-zinc-800`
      : `${base} hover:bg-gray-50 dark:hover:bg-zinc-800`;
  }

  onAttachmentsChange(attachments: SupportAttachment[]): void {
    this.attachments = attachments;
  }

  applyTemplate(templateId: string): void {
    this.selectedTemplateId = templateId;
    const template = this.templates().find((item) => item.id === templateId);
    if (!template) {
      return;
    }
    this.message = applySupportTemplateMessage(template.body, this.selectedUser()?.firstName);
  }

  hasMobile(): boolean {
    return !!this.selectedUser()?.mobile?.trim();
  }

  hasEmail(): boolean {
    return !!this.selectedUser()?.email?.trim();
  }

  hasPushDevice(): boolean {
    return !!this.selectedUser()?.hasPushDevice;
  }

  onSmsChange(event: Event): void {
    if (!this.hasMobile()) {
      return;
    }
    this.alertPreferences = { ...this.alertPreferences, sms: this.isChecked(event) };
  }

  onWhatsappChange(event: Event): void {
    if (!this.hasMobile()) {
      return;
    }
    this.alertPreferences = { ...this.alertPreferences, whatsapp: this.isChecked(event) };
  }

  onEmailChange(event: Event): void {
    if (!this.hasEmail()) {
      return;
    }
    this.alertPreferences = { ...this.alertPreferences, email: this.isChecked(event) };
  }

  onPushChange(event: Event): void {
    if (!this.hasPushDevice()) {
      return;
    }
    this.alertPreferences = { ...this.alertPreferences, push: this.isChecked(event) };
  }

  hasAlertPreference(): boolean {
    return (
      this.alertPreferences.sms ||
      this.alertPreferences.whatsapp ||
      this.alertPreferences.email ||
      this.alertPreferences.push
    );
  }

  formatUserType(userType?: SupportUserType | null): string {
    switch (userType) {
      case 'creator':
        return 'Creator';
      case 'brand':
        return 'Brand';
      case 'agency':
        return 'Agency';
      default:
        return 'User';
    }
  }

  userHandle(user: SupportUserSummary): string {
    if (user.instaHandle?.trim()) {
      return `@${user.instaHandle.trim().replace(/^@/, '')}`;
    }
    if (user.tikTokHandle?.trim()) {
      return `@${user.tikTokHandle.trim().replace(/^@/, '')}`;
    }
    return '';
  }

  adminLabel(admin: SupportAdminOption): string {
    const name = `${admin.firstName} ${admin.lastName}`.trim();
    return name || admin.email;
  }

  close(): void {
    this.closed.emit();
  }

  submit(): void {
    this.error.set('');
    const user = this.selectedUser();
    const subject = this.subject.trim();
    const message = this.message.trim();

    if (!user) {
      this.error.set('Search for and select the person this ticket is for.');
      return;
    }

    if (!this.category) {
      this.error.set('Please select a category.');
      return;
    }

    if (!subject || (!message && this.attachments.length === 0)) {
      this.error.set('Please enter a subject and message or attach a file.');
      return;
    }

    if (!this.hasAlertPreference()) {
      this.error.set('Select at least one way to notify this person.');
      return;
    }

    this.submitting.set(true);
    this.subscriptionList.add(
      this.supportService
        .createOutboundTicket({
          targetUserId: user.userId,
          category: this.category,
          subject,
          message,
          attachments: this.attachments.length ? this.attachments : undefined,
          alertPreferences: this.alertPreferences,
          assignedAdminUserId: this.assignedAdminUserId || null,
        })
        .subscribe({
          next: (ticket) => {
            this.submitting.set(false);
            this.created.emit(ticket);
            this.cdr.markForCheck();
          },
          error: () => {
            this.submitting.set(false);
            this.error.set('Could not create this ticket. Please try again.');
            this.cdr.markForCheck();
          },
        }),
    );
  }

  private isChecked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  /** Support opens the conversation, so channels follow the recipient's details, not the admin's. */
  private defaultAlertPreferences(user: SupportUserSummary): SupportAlertPreferences {
    const hasMobile = !!user.mobile?.trim();
    const hasEmail = !!user.email?.trim();
    // On by default for anyone with the app — it's the channel they're most likely to see.
    const push = !!user.hasPushDevice;

    if (!hasMobile) {
      return { sms: false, whatsapp: false, email: hasEmail, push };
    }

    const isUk = isUkSupportCountry(user.countryCode);
    return { sms: !isUk, whatsapp: isUk, email: false, push };
  }

  private reapplySelectedTemplate(): void {
    if (this.selectedTemplateId) {
      this.applyTemplate(this.selectedTemplateId);
    }
  }

  private resetForm(): void {
    this.searchTerm = '';
    this.category = '';
    this.selectedTemplateId = '';
    this.subject = '';
    this.message = '';
    this.attachments = [];
    this.alertPreferences = { sms: false, whatsapp: false, email: true, push: false };
    this.assignedAdminUserId = this.currentUserService.shoutUser?.firebaseUid ?? '';
    this.results.set([]);
    this.showResults.set(false);
    this.searching.set(false);
    this.searchError.set('');
    this.selectedUser.set(null);
    this.highlightedIndex.set(-1);
    this.submitting.set(false);
    this.error.set('');
  }
}
